/**
 * <order-detail> — the order as a first-class object, opened from
 * <order-card>'s own `show()` on click. Zero-I/O: writes are fired as
 * intents (order-ops.state.ts's Subjects) for a web-side `.sync.ts` to
 * answer, exactly as guest-issue.view.ts fires guestIssueAction$.
 *
 * Body order, top to bottom:
 *   a. Header — the identity header, full-bleed: it spans the sheet's whole
 *      measure and supplies its own inset, so the overlay's own rounded
 *      material clips its top corners and its bottom sweeps into the content
 *      below, exactly as `<event-header>` frames the buyer's own order page.
 *      A non-"paid" status leads as an uppercase eyebrow above the name —
 *      the one fact that changes how every other fact on this sheet reads.
 *      The email line carries the same messenger tick `<order-card>` renders,
 *      so "did they actually get their tickets" is answered here rather than
 *      only back on the list the operator came from.
 *      The ticket count is the header's one dominant metric, and the
 *      order-wide actions ride the trailing end of that same line rather
 *      than claiming a row of their own: Send tickets (acts on the address),
 *      Copy link / Share (act on the order link), Refund (acts on
 *      the money). Each is a
 *      neutral `filled tonal` icon-button carrying its own accessible name
 *      and a tooltip — the same vocabulary the buyer's order page uses for
 *      its own order-wide rail, and the one treatment legible over the
 *      header's own bloom, which an `outlined` control has no ground to sit
 *      against.
 *   b. Order line — one compact line inside the header, under the email:
 *      the invoice as a `filled tonal` control carrying its § 14 number and
 *      total, opening the stored PDF in place as its own overlay (print and
 *      download live in the viewer's toolbar), and beside it in
 *      de-emphasized ink when the order happened, its transaction fee and,
 *      where no document exists yet, why. A summary, never a ledger: the
 *      per-line breakdown is printed in the PDF. A €0 order renders no
 *      invoice, since § 14 obliges none and `fulfillReceipt` consumes no
 *      number for it. The PDF is framed from a same-origin `blob:` a `.sync`
 *      fetched, never the Storage URL: Chrome refuses to render a
 *      cross-origin PDF in an iframe.
 *   c. Tickets — a horizontal rail, one column per ticket carrying the card
 *      the buyer holds and, beneath it, that ticket's own state and action. The card is
 *      `<ticket-card>` (ticket-templates/ticket-card.view.ts) at a fixed
 *      preview width: literally the SAME element the buyer's own order page
 *      mounts, so the display id an operator reads here is the one on the
 *      buyer's screen. It is told `buyerLocale` rather than left on this
 *      operator's UI language — the point of the preview is what the BUYER
 *      holds, and an operator comparing it against a forwarded screenshot
 *      must be looking at the same card. Laid out at its native 384px design
 *      width and shrunk whole via CSS `transform: scale()`. Its type sizes
 *      are absolute, so re-flowing it narrower overflows its `aspect-[2/3]`
 *      box and `overflow-hidden` shears the content; a scaled rendering never
 *      re-flows, so nothing shears.
 *      Each ticket's check-in state is carried by its action alone — "Check
 *      in" and "Undo check-in" each name the state they act on, so a line
 *      beside them states the same fact twice. Check in rides
 *      `tickets.checkin.scan`, Undo check-in the supervisor tier's own
 *      `tickets.checkin.undo` — both fired as intents, written by
 *      `web`'s order-ops.sync.ts through the same
 *      `@momo/ticket-checkin` write the door performs.
 *   d. Close — an icon-button at the header's own top-right, not a footer
 *      row: the overlay supplies the material and no padding, so this sheet
 *      pads itself and never paints a full-bleed layer over the glass
 *      (schmancy's overlay contract).
 *
 * Send tickets opens one sheet holding the address, and the address the
 * operator leaves it with decides what happens: unchanged, it sends to the
 * already-known address on `tickets.reports.view` alone (the floor for opening
 * the orders destination at all) in one direct tap; corrected, it rides its own
 * `tickets.orders.amend` and keeps the second beat, an explicit `confirm()`
 * naming the consequence. The two permissions are independent, so the sheet
 * opens for either one and refuses whichever exit its holder lacks.
 * Copy link is the one honest always-present path to a
 * shareable link (clipboard) once `orderAccessToken` exists, disabled with
 * an honest reason otherwise; Share, native-sheet only, renders only where
 * `navigator.share` exists — Copy link is the fallback every device has.
 * A disabled action keeps its reason in its own accessible name, so the
 * refusal is readable rather than merely dimmed.
 *
 * Purge is the one action that erases rather than changes, and like Refund it
 * is absent rather than disabled when it cannot be taken — without
 * `tickets.orders.purge`, and on any order that is neither a sandbox payment
 * nor a member's own guest issuance. Those two grounds are what carries no
 * money: a paid live order stays out of reach of this control entirely. A
 * dimmed "erase this order" invites an operator to go looking for the
 * authority to press it, which is the opposite of what a destroy control
 * should teach. It takes two beats: a `confirm()` naming everything that
 * disappears, then the intent.
 *
 * Refund is a step this sheet enters rather than a sixth always-on control.
 * The header's own `currency_exchange` action opens it, and while it is open
 * every ticket row keeps its card and its two existing slots: the status line
 * states the refusal for a ticket that cannot be given back, and the trailing
 * control slot carries that ticket's own derived share in place of the
 * check-in button. The row itself becomes the choice — the whole surface
 * toggles that ticket and takes a denser, higher ground while it is chosen,
 * with the checkbox absolutely placed at its top-right corner as the marker
 * of that state rather than the 20px target the choice depends on. Nothing is
 * appended per column and nothing is torn down — the header's invoice line
 * stays mounted, so entering the step never re-fetches the invoice preview or
 * reflows the sheet around it.
 * The running total rides the confirm button's own label rather than a tally
 * line beside it, and the confirm names all three consequences together,
 * because from this side there is no undo: the money leaves, the tickets stop
 * being admitted, and the seats go back on sale.
 *
 * That button's label is the whole refusal vocabulary too, since every reason
 * a selection cannot be sent is a reason about the selection itself: the
 * order's ledger has not answered yet, nothing priced is chosen, or the
 * selection outgrows what the order can still give back. The last one is
 * refused here rather than sent, because the consumer clamps the money to the
 * remaining headroom and still cancels every ticket the marker names — a
 * figure the operator was shown and a set of seats they released would stop
 * agreeing with nothing on either side saying so. A selection priced at
 * nothing is the opposite case and is sent: a comp's tickets void and its
 * seats come back, which is a revocation the confirm names in those words
 * rather than offering €0.00 back.
 *
 * `showFixEmail`/`showInvoice`/`showRefund`/`showPurge` (all default `true`,
 * unaffected for the admin console) let a host narrower than the console lock
 * the send sheet's address to read-only and hide the invoice section, the
 * refund step and the purge action outright — not by
 * permission, since a door-phone account may genuinely hold
 * `tickets.orders.amend` for other reasons and still have no business
 * rewriting a buyer's email from that device, and since
 * `orderInvoicePreviewRequested$`, `refundBatchRequested$` and
 * `orderPurgeRequested$` all have no answering `.sync` outside
 * `web`, so a host with no such sync would otherwise show a preview
 * thumbnail that never resolves, and a refund step and a purge whose confirms
 * write nothing. The manual-lookup screen the door mounts
 * (checkin/manual-lookup/manual-lookup.view.ts) sets all four false.
 */
import { overflowWithin, reducedMotion$ } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm, show } from "@mhmo91/schmancy/overlay";
import { tooltip } from "@mhmo91/schmancy/primitives";
// Side-effect: registers <schmancy-scroll> and <schmancy-scroll-rail>.
import "@mhmo91/schmancy/layout";
import { virtualize } from "@mhmo91/schmancy/virtualizer";
import { resolveLocalizedText } from "@momo/i18n";
import { Order, TicketType } from "@momo/types";
import { css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { when } from "lit/directives/when.js";
import {
	catchError,
	EMPTY,
	finalize,
	from,
	merge,
	takeUntil,
	tap,
	throwError,
} from "rxjs";
import { canViewSaleReports } from "../../admin/sale/sale.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { locationsState } from "../../storefront/locations.state.ts";
import { captureTicketCardImage } from "../../ticket-templates/capture.ts";
import {
	shareTicketCardImage,
	ticketShareFileName,
} from "../../ticket-templates/share.ts";
import type {
	TicketCard,
	TicketCardEvent,
} from "../../ticket-templates/ticket-card.view.ts";
import { workspaceOrigin } from "../../workspace/workspace.state.ts";
// Side-effect: registers <ticket-card>.
import "../../ticket-templates/ticket-card.view.ts";
import { resolveVenueTimezone } from "../events.state.ts";
import { ticketTypesState } from "../ticket-types/ticket-types.state.ts";
import {
	consoleEvent,
	consoleNotLive,
	consoleTickets,
} from "./console.state.ts";
import {
	deliveryTick,
	formatCents,
	invoiceUrl,
	type OrderRow,
	orderStatusTone,
	orderTicketCount,
	orderTotal,
	relativeAgo,
	type TicketRow,
	type Tone,
} from "./order-detail.engine.ts";
import "./order-send-tickets.view.ts";
import {
	canAmendOrders,
	canCheckInTickets,
	canPurgeOrders,
	canRefundOrders,
	canUndoCheckIn,
	openOrderId,
	orderActionSettled$,
	orderInvoicePreviewRequested$,
	orderInvoicePreviews,
	orderPurgeRequested$,
	orderRefundDraft,
	orderRefundSelection,
	type RefundTicketRefusal,
	type RefundTicketRow,
	refundBatchOutcome,
	refundBatchRequested$,
	ticketCheckinRequested$,
	ticketUndoCheckinRequested$,
	toggleAllRefundTickets,
	toggleRefundTicket,
} from "./order-ops.state.ts";
import {
	type TicketRefundReading,
	ticketRefundReadings,
} from "./refunds/ledger.state.ts";
// Side-effect: registers <order-refunds>, opened on demand from a settled
// row's own trailing control rather than mounted standing on the sheet.
import "./refunds/order-refunds.view.ts";

/** `<ticket-card>` emits a fixed pixel width, so one width serves
 *  every breakpoint rather than a fluid `w-*`/`md:w-*` pair a fixed-px card
 *  can't honor. 160 sits in this package's own established preview band —
 *  280 for the gallery (`ticket-template-catalog.view.ts`), 120 for the
 *  picker (`ticket-template-picker.view.ts`) — and stays well under the
 *  card's native 384px design width so it never upscales and blurs. It is
 *  sized for the card alone — the largest preview at which the artifact
 *  still reads as itself, while a whole order's worth of rows still reads
 *  without burying the money below the fold. Its state and action stack
 *  beneath it, so the card's own width is the whole column's measure. */
const TICKET_CARD_PREVIEW_WIDTH = 160;

/** One column's width, to the pixel, not an estimate: the card plus the
 *  column's own `p-2` inset either side (16px). Every column is pinned to it
 *  (`renderBuyerTickets`), which is what lets `virtualize()` size an order's
 *  whole rail without having built it. */
const TICKET_COLUMN_WIDTH = TICKET_CARD_PREVIEW_WIDTH + 16;

const TONE_CLASS = {
	default: "",
	warning: "text-warning-default",
	error: "text-error-default",
} as const satisfies Record<Tone, string>;

// A plain environment capability, read once — not tied to any state() cell,
// so it lives as a module constant rather than a per-render recomputation.
const SUPPORTS_NATIVE_SHARE = typeof navigator.share === "function";

const ORDER_STATUS_KEY = {
	authorised: "admin.events.console.statusAuthorised",
	paid: "admin.events.console.statusPaid",
	refunded: "admin.events.console.statusRefunded",
	cancelled: "admin.events.console.statusCancelled",
} as const satisfies Record<OrderRow["status"], string>;

/** How loudly a ticket's own refund reading reads. Marks are spent where the
 *  ordering cannot report: a settled refund takes a NEUTRAL dim ground rather
 *  than the error tint a cancelled ticket used to get — money going back on
 *  request is a completed, correct outcome and reads as an alarm only by
 *  accident, and a purge is a lawful erasure rather than an incident. The
 *  error tint is kept for the one state that genuinely wants a person: money
 *  that did not come back. Warning is the provider still being asked. */
const READING_TONE = {
	refunded: "neutral",
	confirming: "neutral",
	underWay: "warning",
	failed: "error",
	cancelledNoRefund: "neutral",
	unanswered: "neutral",
} as const satisfies Record<
	TicketRefundReading["kind"],
	"neutral" | "warning" | "error"
>;

/** One line per way a ticket can be out of a refund's reach. Exhaustive over
 *  `RefundTicketRefusal`, which derives its first two members from the ticket
 *  entity's own status union — so a status the model grows fails to compile
 *  here rather than rendering as a selectable row. */
const REFUND_REFUSAL_KEY = {
	redeemed: "admin.events.console.orderDetail.refundRefusalRedeemed",
	cancelled: "admin.events.console.orderDetail.refundRefusalCancelled",
	claimed: "admin.events.console.orderDetail.refundRefusalClaimed",
	unpriced: "admin.events.console.orderDetail.refundRefusalUnpriced",
} as const satisfies Record<RefundTicketRefusal, string>;

@schmancyElement("order-detail")
export class OrderDetail extends LocalizeMixin(SchmancyElement) {
	/** A grid item's default `min-width: auto` sizes the overlay's own track to
	 *  this sheet's 28rem rather than to the width the overlay allocated, and
	 *  the root's `max-w-full` then resolves against that inflated box and
	 *  clamps nothing — the sheet hangs off a phone's left edge. At 0 the track
	 *  keeps the allocated width, which is what `max-w-full` yields to. */
	static styles = [
		css`
			:host {
				min-width: 0;
			}
		`,
	];

	@property({ attribute: false }) order!: OrderRow;

	/** Whether the send sheet lets the address be corrected at all — see this
	 *  class's own header for why a door-context host locks it read-only
	 *  rather than leaving it to `canAmendOrders` alone. */
	@property({ type: Boolean }) showFixEmail = true;

	/** Whether the invoice section renders at all — see this class's own
	 *  header. */
	@property({ type: Boolean }) showInvoice = true;

	/** Whether the refund step is reachable at all — same reason as the two
	 *  above: `refundBatchRequested$` has no answering `.sync` outside
	 *  `web`, so a host that mounts this sheet without one (the door's
	 *  manual lookup) would otherwise offer a control that writes nothing. */
	@property({ type: Boolean }) showRefund = true;

	/** Whether the purge action renders at all — same reason as the three
	 *  above: `orderPurgeRequested$` has no answering `.sync` outside
	 *  `web`. Guest orders are the common case at a door, so the
	 *  eligibility that admits them is exactly what makes this dead control
	 *  reachable there in practice. */
	@property({ type: Boolean }) showPurge = true;

	/** The ticket a share file is currently being composed for — drives that
	 *  one card's share button into a busy, disabled state; mirrors
	 *  order-access.view.ts's own sharingTicketId (per-ticket, not a global
	 *  flag, since composing one card's file says nothing about a sibling
	 *  card's own button). */
	@state() private actions: Readonly<
		Record<
			string,
			{
				readonly requestId: string;
				readonly busy: boolean;
				readonly message: string | null;
			}
		>
	> = {};

	@state() private actionNotice = "";
	@state() private refundSubmitting = false;
	@state() private refundNotice = "";
	private refundAwaiting = false;
	@state() private copyingLink = false;
	@state() private sharingLink = false;
	@state() private sharingTicketId: string | null = null;

	/** A rasterize kicked off on the share button's own `pointerdown`, before
	 *  the `click` that actually shares — see order-access.view.ts's own
	 *  warmCapture doc for why the head start matters on iOS Safari's gesture
	 *  window. */
	private warmCapture: { ticketId: string; image: Promise<Blob> } | null = null;

	/** Three rows, nothing between them: the header and the refund footer each
	 *  take the sheet's whole measure with no inset of their own to sit inside,
	 *  and the scroll body between them is the only thing that pads.
	 *  `minmax(0,1fr)` rather than `1fr` — a grid row's default
	 *  `min-height: auto` refuses to shrink below its content, which would push
	 *  the scroller past the overlay's own height instead of scrolling inside
	 *  it.
	 *
	 *  The body holds the ticket rail alone: the invoice and when the order
	 *  happened ride the header as one compact line, so nothing stands between
	 *  the identity and the seats, and nothing trails them. The rail scrolls
	 *  on its own axis; the body scrolls vertically only where a short
	 *  viewport cannot fit one column's height.
	 *
	 *  The footer is its own row rather than a `sticky bottom-0` last child of
	 *  the scroller, because a sticky element stays in the scroll region's flow
	 *  and paints over whatever content shares the scrollport's bottom band at
	 *  the current offset: every ticket row passes behind it for ~70px of
	 *  scrolling, and over that band the row's checkbox is hit-tested away to
	 *  the footer and the tap does nothing. Bottom padding on the scroll content
	 *  cannot answer that — it only clears the content's tail, while the rows
	 *  the operator is choosing between are mid-list. A third `auto` track is
	 *  0px while the refund step is closed, so taking the footer out of the flow
	 *  strands no dead space either.
	 *
	 *  A definite `w-md`, not `w-full max-w-md`: the overlay's anchored
	 *  surface is `w-max`, so a percentage width resolves back to whatever
	 *  this content happens to measure — every order would open at its own
	 *  width, and checking a ticket in would resize the sheet under the
	 *  operator's hand as that row's button swapped. `max-w-full` then clamps
	 *  that 28rem to the narrower width the overlay's sheet layout allocates
	 *  on a phone — but only because the host declares `min-width: 0` (see
	 *  this class's own styles); without it the allocated width never reaches
	 *  this element and the percentage resolves against 28rem itself. */
	override render() {
		const o = this.order;
		const draft = orderRefundDraft.get();
		// Null unless the refund step is open on THIS order — the draft cell is
		// module-scoped and one sheet at a time composes against it.
		const refund = draft?.order.id === o.id ? draft : null;
		return html`
			<div class="mx-auto grid h-full w-md max-w-full grid-rows-[auto_minmax(0,1fr)_auto]">
				${this.renderHeader(o, refund !== null)}
				<div ${overflowWithin()} class="px-6 py-6">
					${this.renderBuyerTickets(o, refund?.kind === "composing" ? refund.rows : null)}
				</div>
				${when(
					refund,
					(r) => html`
						<schmancy-surface
							data-refund-bar
							type="containerLow"
							opacity="translucent"
							elevated
							class="flex flex-col gap-2"
						>
							${when(consoleNotLive.get(), () => html`<schmancy-typography role="status" type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.notLive")}</schmancy-typography>`)}
							${
								r.kind === "ticketsUnreadable"
									? html`
									<schmancy-typography type="body" token="sm" class="min-w-0 flex-1 text-error-default">
										${this.t("admin.events.console.orderDetail.refundTicketsUnreadable")}
									</schmancy-typography>
									<schmancy-button
										variant="outlined"
										color="neutral"
										@click=${() => orderRefundSelection.replace({ orderId: null, ticketIds: [] })}
										>${this.t("admin.common.cancel")}</schmancy-button
									>
								`
									: html`
									<div class="flex items-center justify-end gap-2">
										${when(
											r.selectableCount > 0,
											() => html`
												<!-- Leading, and the only control on this bar that is
												     not the decision itself: a block order carries up to
												     a thousand tickets and taking them one at a time was
												     the whole flow. -->
												<schmancy-button
													variant="text"
													color="neutral"
													class="mr-auto"
													@click=${() => toggleAllRefundTickets()}
													>${this.t(
														r.allSelectableSelected
															? "admin.events.console.orderDetail.refundSelectNone"
															: "admin.events.console.orderDetail.refundSelectAll",
													)}</schmancy-button
												>
											`,
										)}
										<schmancy-button
											variant="outlined"
											color="neutral"
											@click=${() => orderRefundSelection.replace({ orderId: null, ticketIds: [] })}
											>${this.t("admin.common.cancel")}</schmancy-button
										>
									</div>
									<!--
										The decision spans the bar on its own row rather than sharing
										one with Cancel: a fixed measure beside content-sized buttons
										holds only while the words in THOSE stay the length someone
										measured, and German ran that row past the sheet and grew the
										whole bar on a language toggle. Spanning it, neither row can
										wrap in any language or viewport.
									-->
									<schmancy-button
										variant="filled"
										color="error"
										width="full"
										.busy=${this.refundSubmitting}
                                        ?disabled=${r.block !== null}
										@click=${() => {
											if (this.refundSubmitting || r.block !== null) return;
											this.refundSubmitting = true;
											this.refundNotice = "";
											// A working set priced at nothing is a revocation, not a
											// refund of nothing: no money moves, the tickets void and
											// the seats come back — so the confirm names that
											// consequence rather than offering €0.00 back.
											const revoking = r.payoutCents === 0;
											// The plain sum of the rows the operator chose, which is
											// what the consumer moves and what each of those rows
											// already printed.
											const amount = formatCents(r.payoutCents);
											from(
												confirm({
													title: this.t(
														revoking
															? "admin.events.console.orderDetail.refundRevokeConfirmTitle"
															: "admin.events.console.orderDetail.refundConfirmTitle",
														{ amount },
													),
													message: this.t(
														revoking
															? r.selectedCount === 1
																? "admin.events.console.orderDetail.refundRevokeConfirmBodySingular"
																: "admin.events.console.orderDetail.refundRevokeConfirmBodyPlural"
															: r.selectedCount === 1
																? "admin.events.console.orderDetail.refundConfirmBodySingular"
																: "admin.events.console.orderDetail.refundConfirmBodyPlural",
														{
															amount,
															buyer: o.buyerName,
															count: r.selectedCount,
														},
													),
													confirmText: this.t(
														revoking
															? "admin.events.console.orderDetail.refundRevokeSubmit"
															: "admin.events.console.orderDetail.refundSubmit",
														{ amount },
													),
													cancelText: this.t("admin.common.cancel"),
													variant: "danger",
												}),
											)
												.pipe(takeUntil(this.disconnecting))
												.subscribe((ok) => {
													if (!ok) {
														this.refundSubmitting = false;
														return;
													}
													// No toast here: the write has not been accepted yet,
													// and order-ops.sync.ts answers on refundBatchOutcome
													// once it has — or reports the refusal instead.
													this.refundAwaiting = true;
													refundBatchRequested$.next(r.selection);
												});
										}}
									>
										${choose(
											r.block,
											[
												[
													"ledgerPending",
													() =>
														this.t(
															"admin.events.console.orderDetail.refundLedgerPending",
														),
												],
												[
													"nothingSelected",
													() =>
														this.t(
															"admin.events.console.orderDetail.refundEmpty",
														),
												],
											],
											() =>
												r.payoutCents === 0
													? this.t(
															"admin.events.console.orderDetail.refundRevokeSubmit",
														)
													: this.t(
															"admin.events.console.orderDetail.refundSubmit",
															{
																amount: formatCents(r.payoutCents),
															},
														),
										)}
									</schmancy-button>
								`
							}
                            <schmancy-typography role="status" type="body" token="sm" class="h-12 px-6 text-error-default">${this.refundNotice}</schmancy-typography>
						</schmancy-surface>
					`,
				)}
			</div>
		`;
	}

	/** The sheet mounts through `show()` with its order as a property, so this
	 *  cell is the only place outside it that can name the order on screen —
	 *  and `web`'s order-ops.sync.ts keys this order's refund ledger
	 *  on it, which is what makes a cancelled row able to say why it is
	 *  cancelled on a plain order open rather than only inside the refund
	 *  step. */
	override connectedCallback(): void {
		super.connectedCallback();
		merge(
			orderActionSettled$.pipe(
				tap((answer) => {
					const held = Object.entries(this.actions).find(
						([, value]) => value.requestId === answer.requestId,
					);
					if (!held) return;
					this.actions = {
						...this.actions,
						[held[0]]: {
							requestId: answer.requestId,
							busy: false,
							message: answer.message,
						},
					};
					if (held[0] === `purge:${this.order.id}` && answer.message === null)
						this.dispatchEvent(
							new CustomEvent("close", { bubbles: true, composed: true }),
						);
				}),
			),
			refundBatchOutcome.$.pipe(
				tap((answer) => {
					if (
						!this.refundAwaiting ||
						(answer?.kind !== "accepted" && answer?.kind !== "refused")
					)
						return;
					this.refundAwaiting = false;
					this.refundSubmitting = false;
					if (answer.kind === "refused") {
						this.refundNotice = this.t(
							"admin.events.console.orderDetail.refundFailed",
						);
						return;
					}
					orderRefundSelection.replace({ orderId: null, ticketIds: [] });
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
		openOrderId.replace(this.order?.id ?? null);
	}

	/** The refund step is composed against ONE order, so leaving this sheet
	 *  leaves it — otherwise the next order opened would inherit a selection
	 *  made against a different set of tickets. The open-order cell is cleared
	 *  on the same test and for the same reason: a sheet that has gone must not
	 *  keep a listener open on the order it was showing. */
	override disconnectedCallback(): void {
		super.disconnectedCallback();
		if (orderRefundSelection.value.orderId === this.order?.id) {
			orderRefundSelection.replace({ orderId: null, ticketIds: [] });
		}
		if (openOrderId.value === this.order?.id) openOrderId.replace(null);
	}

	private renderHeader(o: OrderRow, refunding: boolean) {
		const tick = deliveryTick(o);
		const deliveryLabel = this.t(`admin.events.console.${tick.labelKey}`);
		const tone = orderStatusTone(o.status);
		const count = orderTicketCount(o);
		const total = orderTotal(o);
		const sandbox = o.payment?.env === "sandbox";
		// What it was, what it came to, when — "Issued" for a comp, since the
		// buyer paid for nothing and "Purchased" would be untrue.
		const summary = [
			`${count} ${this.t(
				count === 1
					? "admin.events.console.orderDetail.ticketCountSingular"
					: "admin.events.console.orderDetail.ticketCountPlural",
			)}`,
			total === 0 ? "" : formatCents(total),
			`${this.t(
				total === 0
					? "admin.events.console.orderDetail.issued"
					: "admin.events.console.orderDetail.purchased",
				{ relative: relativeAgo(o.authorisedAt.toDate(), this.uiLanguage) },
			)}${o.origin === "guest" ? this.t("admin.events.console.orderDetail.guestTicketSuffix") : ""}`,
		]
			.filter((part) => part !== "")
			.join(" · ");
		const notice = this.actions[`purge:${o.id}`]?.message ?? this.actionNotice;
		return html`
			<schmancy-surface
				type="container"
				opacity="translucent"
				class="relative flex flex-col gap-5 overflow-hidden rounded-b-bezel p-6"
			>
				<schmancy-icon-button
					icon="close"
					size="sm"
					color="neutral"
					class="absolute right-4 top-4"
					aria-label=${this.t("admin.common.close")}
					${tooltip(this.t("admin.common.close"))}
					@click=${() => this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }))}
				></schmancy-icon-button>

				<div class="flex flex-col gap-1 pr-10">
					${when(
						tone !== "default" || sandbox,
						() => html`
							<schmancy-typography
								type="label"
								token="sm"
								transform="uppercase"
								class=${this.classMap({
									"tracking-widest": true,
									[TONE_CLASS[tone === "default" ? "warning" : tone]]: true,
								})}
							>
								${[
									tone === "default" ? "" : this.t(ORDER_STATUS_KEY[o.status]),
									sandbox
										? this.t("admin.events.console.orderDetail.testOrder")
										: "",
								]
									.filter((part) => part !== "")
									.join(" · ")}
							</schmancy-typography>
						`,
					)}
					<schmancy-typography type="headline" token="md" class="wrap-break-word">${o.buyerName}</schmancy-typography>
					<div class="flex min-w-0 items-center gap-2">
						<schmancy-typography
							type="body"
							token="sm"
							class=${this.classMap({
								"min-w-0 truncate": true,
								"text-error-default": tick.bounced,
								"text-surface-onVariant": !tick.bounced,
							})}
						>
							${o.buyerEmail}
						</schmancy-typography>
						<!--
							The same tick-and-word pair <order-card> renders on the row this
							sheet opened from: tone on the tick alone, the word plain
							rendered text a screen reader already reads, error tone on both
							the moment the address is dead.
						-->
						<span class="flex shrink-0 items-center gap-1">
							<schmancy-icon class=${this.classMap({ "text-base": true, [tick.toneClass]: true })}
								>${tick.icon}</schmancy-icon
							>
							<schmancy-typography
								type="label"
								token="sm"
								class=${this.classMap({ "text-error-default": tick.bounced })}
								>${deliveryLabel}</schmancy-typography
							>
						</span>
					</div>
				</div>

				<!--
					One quiet line for the order's whole summary, one row for what can be
					done with it. The line holds one row in every language: a sentence
					that wraps in German would push the rail under it on a language
					toggle, so it truncates and keeps its whole text in a tooltip, while
					a screen reader reads it whole regardless.
				-->
				<div class="flex flex-col gap-3">
					<schmancy-typography type="title" token="sm" class="truncate tabular-nums" ${tooltip(summary)}>${summary}</schmancy-typography>
					${this.renderActions(o, refunding)}
				</div>
				<!--
					The live region stays mounted so a refusal is announced the moment
					it lands, but takes no room until it has one: an empty line
					reserved under the header was a 48px band of nothing.
				-->
				<schmancy-typography role="status" type="body" token="sm" class=${notice ? "text-error-default" : "sr-only"}>${notice}</schmancy-typography>
			</schmancy-surface>
		`;
	}

	/** The order-wide actions, one row under the summary line, in the order
	 *  they act on: the document, the address, the link, then the money. One
	 *  control owns the address — sending the tickets and correcting the
	 *  address they go to are the same errand, so the operator opens one sheet
	 *  and the address they leave it with decides which of the two happened.
	 *
	 *  Refund is the one action that is absent rather than disabled when it
	 *  cannot be taken. The other three refuse in place and say why, because
	 *  "you lack this permission" is worth telling an operator who can see the
	 *  order; giving money back is not an affordance a member without
	 *  `tickets.orders.refund` should read as merely locked, and an order that
	 *  holds no captured payment has nothing to give back at all — the same
	 *  `status: 'paid'` floor the consumer itself enforces. */
	private renderActions(o: OrderRow, refunding: boolean) {
		const canOpenSend =
			canViewSaleReports.get() || (this.showFixEmail && canAmendOrders.get());
		const canCopyLink = !!o.orderAccessToken;
		return html`
			<div class="flex flex-wrap items-center gap-2">
				${this.renderInvoiceAction(o)}
				${this.action(
					"forward_to_inbox",
					canOpenSend
						? this.t("admin.events.console.orderDetail.sendTicketsAria")
						: this.t(
								"admin.events.console.orderDetail.sendTicketsNoPermissionAria",
							),
					!canOpenSend,
					"filled tonal",
					() => this.sendTickets(o),
				)}
				${this.action(
					"link",
					canCopyLink
						? this.t("admin.events.console.orderDetail.copyLinkAria")
						: this.t("admin.events.console.orderDetail.noShareableLinkAria"),
					!canCopyLink,
					"filled tonal",
					() => this.copyOrderLink(o),
					this.copyingLink,
				)}
				${when(canCopyLink && SUPPORTS_NATIVE_SHARE, () =>
					this.action(
						"ios_share",
						this.t("admin.events.console.orderDetail.shareAria"),
						false,
						"filled tonal",
						() => this.shareOrderLink(o),
						this.sharingLink,
					),
				)}
				${when(
					this.showRefund &&
						canRefundOrders.get() &&
						o.status === "paid" &&
						o.origin !== "guest",
					() =>
						this.action(
							"currency_exchange",
							this.t(
								refunding
									? "admin.events.console.orderDetail.refundExitAria"
									: "admin.events.console.orderDetail.refundAria",
							),
							false,
							"filled tonal",
							() =>
								orderRefundSelection.replace({
									orderId: refunding ? null : o.id,
									ticketIds: [],
								}),
						),
				)}
				${when(
					this.showPurge &&
						canPurgeOrders.get() &&
						(o.payment?.env === "sandbox" ||
							o.origin === "guest" ||
							orderTotal(o) === 0),
					() =>
						this.action(
							"delete_forever",
							this.t("admin.events.console.orderDetail.purgeAria"),
							false,
							"filled tonal",
							() => this.purge(o),
							this.actions[`purge:${o.id}`]?.busy ?? false,
						),
				)}
			</div>
		`;
	}

	/** Two beats, never one, and the second names what disappears: the purge
	 *  deletes the order, its tickets, its link and its stored files outright,
	 *  and nothing on either side of this sheet brings any of it back. The
	 *  callable re-reads the order server-side and refuses anything that is
	 *  neither `payment.env === "sandbox"`, nor `origin === "guest"`, nor
	 *  priced at nothing by its own lines, so this gesture cannot reach a paid
	 *  live order however stale the row it was fired from. */
	private purge(o: OrderRow): void {
		const eventId = consoleEvent.get()?.id;
		if (!eventId || this.actions[`purge:${o.id}`]?.busy) return;
		const requestId = crypto.randomUUID();
		this.actions = {
			...this.actions,
			[`purge:${o.id}`]: { requestId, busy: true, message: null },
		};
		from(
			confirm({
				title: this.t("admin.events.console.orderDetail.purgeConfirmTitle"),
				message: this.t(
					orderTicketCount(o) === 1
						? "admin.events.console.orderDetail.purgeConfirmBodySingular"
						: "admin.events.console.orderDetail.purgeConfirmBodyPlural",
					{ count: orderTicketCount(o) },
				),
				confirmText: this.t("admin.events.console.orderDetail.purgeConfirmCta"),
				cancelText: this.t("admin.events.console.orderDetail.purgeCancel"),
				variant: "danger",
			}),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe((ok) => {
				if (!ok) {
					this.actions = {
						...this.actions,
						[`purge:${o.id}`]: { requestId, busy: false, message: null },
					};
					return;
				}
				orderPurgeRequested$.next({ requestId, eventId, orderId: o.id });
			});
	}

	/** Every action on this sheet, header rail and ticket row alike — one
	 *  vocabulary, so a control means the same thing wherever it sits. `label`
	 *  is both the accessible name and the tooltip, so an icon is never a
	 *  glyph the operator has to guess at, and a disabled action states its
	 *  reason rather than merely dimming.
	 *
	 *  Each caller names the `variant` its own ground needs, because the two
	 *  grounds on this sheet take opposite treatments. `filled tonal` resolves
	 *  to the SAME fill as a `containerLow` surface — on the header's stronger
	 *  `container` ground it reads as a disc, and on a ticket row it would be
	 *  invisible, a control the eye reads as a bare glyph and the hand doesn't
	 *  try to press. So the header takes `filled tonal`, and a row takes
	 *  `outlined`, whose ring is exactly the edge its flat surface lacks. */
	private action(
		icon: string,
		label: string,
		disabled: boolean,
		variant: "filled tonal" | "outlined",
		onClick: () => void,
		busy = false,
	) {
		return html`
			<schmancy-icon-button
				icon=${icon}
				size="sm"
				variant=${variant}
				color="neutral"
				?disabled=${disabled}
                .busy=${busy}
				aria-label=${label}
				${tooltip(label)}
				@click=${onClick}
			></schmancy-icon-button>
		`;
	}

	/** No heading over this rail: a row of literal ticket cards announces
	 *  what it is, and the header above already counts them.
	 *
	 *  One horizontal rail, the same one the buyer's own order page carries
	 *  (order-access.view.ts's `ticketRail`): `<schmancy-scroll-rail>` over a
	 *  scrollbar-less `<schmancy-scroll>`, so the cut shades and a chevron
	 *  steps one column wherever more tickets lie past it, and an order small
	 *  enough to fit carries none of it. A rail costs one column's height
	 *  however many seats the order holds.
	 *
	 *  Each column is the card, then that ticket's own state and action
	 *  beneath it. Ground carries status: a redeemed ticket takes a success
	 *  tint and a cancelled one an error tint, both at 30% so the tint stays a
	 *  signal over the sheet's translucent ground rather than flooding it.
	 *  Elevation is spent only on the refund step's selected column.
	 *
	 *  An order with no tickets says which of the two mutually-exclusive
	 *  reasons produced that — cancelled before fulfillment ever ran, or
	 *  still waiting on minting — rather than rendering a silent gap under
	 *  the header, which reads as a broken screen.
	 *
	 *  The rail windows through `virtualize()` on the horizontal axis, not
	 *  `repeat()`: a large block order can carry hundreds of columns, each a
	 *  whole `<ticket-card>`, and building every one regardless of scroll
	 *  position is the runaway DOM this view exists not to ship. Every column
	 *  is pinned to `TICKET_COLUMN_WIDTH`, so the rendered window lands at its
	 *  true scroll offset. `<schmancy-scroll>`'s host is its own scrollport,
	 *  which is the ancestor the directive windows against. */
	private renderBuyerTickets(
		o: OrderRow,
		refundRows: ReadonlyMap<string, RefundTicketRow> | null,
	) {
		const event = consoleEvent.get();
		const tickets = consoleTickets.value.filter((t) => t.orderId === o.id);
		if (tickets.length === 0)
			return html`
				<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
					${this.t(
						o.status === "cancelled"
							? "admin.events.console.orderDetail.ticketsCancelled"
							: "admin.events.console.orderDetail.ticketsPending",
					)}
				</schmancy-typography>
			`;
		if (!event) return html``;
		const location = locationsState.value.find(
			(l) => l.id === event.locationId,
		);
		const tz = resolveVenueTimezone(event.locationId, locationsState.value);
		const count = tickets.length;
		// What this order's own refund ledger says about each of its seats —
		// empty on a host that mounts no refund `.sync` to answer with, where a
		// cancelled row keeps the one word it has always had rather than waiting
		// forever on a listener nobody opened.
		const readings = this.showRefund ? ticketRefundReadings.get() : null;
		return html`
				<schmancy-scroll-rail>
				<schmancy-scroll direction="horizontal" hide>
					<div class="flex w-max items-start gap-3 pb-2">
						${virtualize(
							tickets,
							(t) => t.id,
							(t, i) => {
								const type = ticketTypesState.value.find(
									(ty) => ty.id === t.typeId,
								);
								// The id is the last resort, never a name in a language this
								// operator does not read: the console names a type the same way
								// every other line on this sheet is named.
								const typeName =
									type === undefined
										? t.typeId
										: resolveLocalizedText(
												type.nameByLocale,
												type.name,
												this.uiLanguage,
											);
								const variant = TicketType.resolveTicketTemplateVariant(
									{ templateVariant: type?.templateVariant ?? null },
									{ templateVariant: event.templateVariant },
								);
								// What this surface holds that the buyer's page cannot: the
								// live Event and Location docs, in place of the venue
								// snapshot OrderAccess froze at fulfillment. `location` is
								// genuinely optional — the door's own manual-lookup host
								// leaves `locationsState` unpopulated rather than adding a
								// Firestore read just for this preview (scanner's
								// order-detail.sync.ts), so the card renders with a blank
								// venue line rather than not at all.
								const cardEvent: TicketCardEvent = {
									name: event.name,
									logoUrl: event.logoUrl,
									start: event.schedule.start.toDate(),
									end: event.schedule.end.toDate(),
									timezone: tz,
									venue: {
										name: location?.name ?? "",
										address: location?.address ?? "",
										notes: location?.notes ?? null,
									},
								};
								const canShare = t.status === "valid";
								const sharing = this.sharingTicketId === t.id;
								const refundRow = refundRows?.get(t.id) ?? null;
								// In the refund step the column IS the control: a whole surface
								// the operator can hit anywhere, with the checkbox a marker at
								// its corner rather than the 20px target the choice depends on.
								// A row the draft refuses binds no handler at all — `undefined`
								// is Lit's own "no listener", so a refused row keeps no cursor,
								// no press and nothing to hit-test, exactly as outside the step.
								// A chosen row carries that on its own ground rather than in the
								// checkbox alone: both tonal tier AND fill density, because one
								// tier at the glass scale's 50% fill is a difference the eye
								// loses between neighbours in a stack this dense.
								const selectable = refundRow?.refusal === null;
								const reading = readings?.get(t.id) ?? null;
								// One ground per seat, and the sentence beside it is what tells
								// two settled outcomes apart. A chosen row wears its own ground
								// instead — the choice outranks the state it is in.
								const tone = refundRow?.selected
									? "none"
									: t.status === "redeemed"
										? "admitted"
										: reading !== null
											? READING_TONE[reading.kind]
											: t.status === "cancelled"
												? "error"
												: "none";
								return html`
								<schmancy-surface
									type=${refundRow?.selected ? "containerHighest" : "containerLow"}
									opacity=${refundRow?.selected ? "solid" : "translucent"}
									?elevated=${!!refundRow?.selected}
									style=${`width:${TICKET_COLUMN_WIDTH}px`}
									class=${this.classMap({
										"relative flex flex-col gap-3 rounded-2xl p-2": true,
										// A whole-column tap target full of selectable text: without this an
										// iOS long-press raises the selection callout over the ticket
										// instead of choosing it.
										"cursor-pointer select-none touch-manipulation": selectable,
										// The row is the control, so it answers the pointer on its own
										// ground — one tier toward the chosen state, never applied to a
										// row already at containerHighest, which this would step DOWN.
										"hover:bg-surface-containerHigh":
											selectable && !refundRow?.selected,
										"bg-success-container/30": tone === "admitted",
										"bg-surface-containerHigh/60": tone === "neutral",
										"bg-warning-container/30": tone === "warning",
										"bg-error-container/30": tone === "error",
									})}
									@click=${selectable ? () => toggleRefundTicket(t.id) : undefined}
								>
									<div data-ticket-card=${t.id} class="relative flex-none">
										<div class=${this.classMap({ "opacity-40 grayscale": !canShare })}>
											<ticket-card
												.ticket=${{
													ticketId: t.id,
													typeName,
													qrToken: t.qrToken,
													templateVariant: variant,
												}}
												.event=${cardEvent}
												.sequence=${count > 1 ? { index: i + 1, count } : null}
												.width=${TICKET_CARD_PREVIEW_WIDTH}
												.holderLocale=${o.buyerLocale}
												capture
											></ticket-card>
										</div>
										<!--
											\`filled tonal\`, not \`outlined\` — the same variant the buyer's
											own page uses on the same corner of the same card. A control
											floating over the ticket must bring its own opaque ground: the
											card paints its own light ground and is theme-independent by
											design (a ticket is an identity document), while this button's
											colors resolve against the APP's theme. \`outlined\` has no
											ground of its own, so under the dark admin theme it drew pale
											cream ink (#D4C4B5) on the card's near-white — about 1.3:1, an
											invisible glyph inside a dark ring.
										-->
										<schmancy-icon-button
											icon=${sharing ? "progress_activity" : "ios_share"}
											size="sm"
											variant="filled tonal"
											color="neutral"
											class=${this.classMap({
												"absolute bottom-2 right-2 shadow-md": true,
												"animate-spin": sharing && !reducedMotion$.value,
											})}
											?disabled=${!canShare}
                                            .busy=${sharing}
											aria-label=${canShare ? `${typeName}: Share ticket` : `${typeName}: Can no longer be shared`}
											title=${canShare ? "Share ticket" : "Can no longer be shared"}
											@pointerdown=${() => this.warmShareCapture(t)}
											@click=${(e: Event) => {
												// Sharing sits inside what is, in the refund step, a
												// clickable row: without this the same tap would also
												// pick the ticket the operator only meant to send.
												e.stopPropagation();
												this.shareTicket(t, event.name, typeName);
											}}
										></schmancy-icon-button>
									</div>
									${this.renderTicketAside(t, typeName, i, count, refundRow, reading)}
									${when(
										refundRow,
										(r) => html`
											<!--
												The checkbox stays the accessible control — the row
												carries no competing role, so a keyboard Space still
												arrives here as \`change\`. It also owns its own click:
												md-checkbox toggles itself and emits that same
												\`change\`, so letting the click reach the row's handler
												too would toggle twice and net to nothing.
											-->
											<schmancy-checkbox
												class="absolute right-3 top-3"
												?disabled=${r.refusal !== null}
												.value=${r.selected}
												aria-label=${
													r.share === 0
														? this.t(
																"admin.events.console.orderDetail.refundTicketRevokeAria",
																{ typeName, index: i + 1, count },
															)
														: this.t(
																"admin.events.console.orderDetail.refundTicketAria",
																{
																	typeName,
																	index: i + 1,
																	count,
																	amount: formatCents(r.share ?? 0),
																},
															)
												}
												@click=${(e: Event) => e.stopPropagation()}
												@change=${(e: CustomEvent<{ value: boolean }>) => {
													if (e.detail.value === r.selected) return;
													toggleRefundTicket(t.id);
												}}
											></schmancy-checkbox>
										`,
									)}
								</schmancy-surface>
							`;
							},
							{
								direction: "horizontal",
								estimateSize: () => TICKET_COLUMN_WIDTH,
							},
						)}
					</div>
				</schmancy-scroll>
			</schmancy-scroll-rail>
		`;
	}

	/** A ticket's own state and its own action, beside its own card: the type,
	 *  the state it is in, and one labeled button — read in that order, each
	 *  starting at the same left edge, down one column. The reading order IS
	 *  the layout, so nothing is pushed to an opposite edge for the eye to
	 *  travel to.
	 *
	 *  A label, not a bare icon. The aside column carries the row's own
	 *  ~250px of free width, so a circular icon-only control read as a
	 *  cryptic afterthought floating beside the status line rather than the
	 *  row's one real action.
	 *
	 *  The check-in state is never written out as a line: "Check in" and "Undo
	 *  check-in" each already name the state they act on, so a line beside them
	 *  says the same thing twice. The status line survives only for what no
	 *  control states — a cancelled ticket, or a refund refusal.
	 *
	 *  Check-in is one direct tap; undo is two beats and supervisor-only —
	 *  reversing an admission is the one act here that rewrites a door's
	 *  record, so it names the consequence in a confirm before it happens.
	 *
	 *  In the refund step the SAME two slots carry refund content rather than
	 *  growing a third: the status line states the refusal for a row that
	 *  cannot be chosen, and the trailing control slot holds this ticket's own
	 *  share in place of the check-in button. Choosing the ticket is the row's
	 *  own job rather than this aside's — the whole surface toggles it and
	 *  wears the checkbox at its corner (renderBuyerTickets) — so a row that
	 *  cannot be refunded has nothing here to press at all and says why on its
	 *  status line: a redeemed ticket refuses the whole marker server-side, so
	 *  it must be unreachable rather than submittable and refused after the
	 *  fact.
	 *
	 *  That share leads with WHAT COMES BACK, and where the seat gives back
	 *  less than its printed price — a code took something off the order, or
	 *  an earlier refund already spent what this seat was worth — that price
	 *  rides under it struck through: one glyph, no label, decoded for the
	 *  whole list by the sentences above the rows. Where the seat gives back
	 *  exactly what is printed on it the two figures are equal and only one
	 *  line renders, so an ordinary row is untouched.
	 *
	 *  And the ledger's own reading outranks the refusal wherever it has one.
	 *  `cancelled` is an outcome, and the operator looking at the seat is owed
	 *  the CAUSE: refunded on this day for this amount, still in flight against
	 *  a seat that admits at the door, failed with nothing back, or cancelled
	 *  with no refund anywhere — which is the purge, the other and only other
	 *  writer of that word. The § 14 correction number stays out of the row: it
	 *  identifies an accounting document, and an operator reaches for it when
	 *  answering the books, never when answering what happened to this ticket.
	 *  It is written down once, behind this row's own `receipt_long` control —
	 *  the seat whose money it moved, and the only place the question is asked. */
	private renderTicketAside(
		t: TicketRow,
		typeName: string,
		index: number,
		count: number,
		refundRow: RefundTicketRow | null,
		reading: TicketRefundReading | null,
	) {
		const eventId = consoleEvent.get()?.id;
		const checkedIn = t.status === "redeemed";
		const checkable =
			t.status === "valid" && canCheckInTickets.get() && !!eventId;
		const refusalKey = refundRow?.refusal
			? REFUND_REFUSAL_KEY[refundRow.refusal]
			: null;
		// The one-line refusal survives only where the ledger has nothing of its
		// own to say — a redeemed seat, a line whose ticket count disagrees with
		// its qty, or a host with no refund `.sync` at all.
		const statusKey =
			reading !== null
				? null
				: (refusalKey ??
					(t.status === "cancelled"
						? ("admin.events.console.ticketCancelled" as const)
						: null));
		return html`
			<div class="flex min-w-0 flex-col items-start gap-2 px-1 pb-1">
				<schmancy-typography type="title" token="sm" lang=${this.uiLanguage} class="hyphens-auto wrap-break-word">${typeName}</schmancy-typography>
				${when(
					statusKey,
					(key) => html`
						<schmancy-typography type="body" token="sm" class="text-error-default">
							${this.t(key)}
						</schmancy-typography>
					`,
				)}
				${when(reading, (rd) => {
					const recordLabel = this.t(
						"admin.events.console.orderDetail.refundRecordAria",
						{ typeName, index: index + 1, count },
					);
					return html`
						<div class="grid w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-2 md:flex">
							<schmancy-icon
								aria-hidden="true"
								class=${this.classMap({
									"text-warning-default": READING_TONE[rd.kind] === "warning",
									"text-error-default": READING_TONE[rd.kind] === "error",
								})}
								>${
									rd.kind === "refunded"
										? "currency_exchange"
										: rd.kind === "failed"
											? "error"
											: rd.kind === "cancelledNoRefund"
												? "block"
												: rd.kind === "unanswered"
													? "hourglass_top"
													: "schedule"
								}</schmancy-icon
							>
							<div class="flex min-w-0 flex-1 flex-col">
								<schmancy-typography
									type="title"
									token="sm"
									class=${this.classMap({
										"tabular-nums": true,
										"text-warning-default": READING_TONE[rd.kind] === "warning",
										"text-error-default": READING_TONE[rd.kind] === "error",
									})}
									>${
										rd.kind === "refunded"
											? rd.amountCents === null
												? this.t(
														"admin.events.console.orderDetail.ticketRefunded",
													)
												: this.t(
														"admin.events.console.orderDetail.ticketRefundedAmount",
														{ amount: formatCents(rd.amountCents) },
													)
											: rd.kind === "confirming"
												? this.t(
														"admin.events.console.orderDetail.ticketRefunded",
													)
												: rd.kind === "underWay"
													? this.t(
															"admin.events.console.orderDetail.ticketRefundUnderWay",
														)
													: rd.kind === "failed"
														? this.t(
																"admin.events.console.orderDetail.ticketRefundFailed",
															)
														: this.t("admin.events.console.ticketCancelled")
									}</schmancy-typography
								>
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${
										rd.kind === "refunded"
											? [
													// Day and month, the same reading the record behind
													// this row's own control gives the claim, so a seat
													// and the claim that moved its money name one date.
													rd.settledAt === null
														? ""
														: new Intl.DateTimeFormat(
																this.uiLanguage === "de" ? "de-DE" : "en-GB",
																{ day: "numeric", month: "long" },
															).format(rd.settledAt),
													rd.alsoCovers === 0
														? ""
														: this.t(
																rd.alsoCovers === 1
																	? "admin.events.console.orderDetail.ticketRefundAlsoCoversSingular"
																	: "admin.events.console.orderDetail.ticketRefundAlsoCoversPlural",
																{ count: rd.alsoCovers },
															),
												]
													.filter((part) => part !== "")
													.join(" · ")
											: rd.kind === "confirming"
												? this.t(
														"admin.events.console.orderDetail.ticketRefundConfirming",
													)
												: rd.kind === "underWay"
													? this.t(
															rd.refused
																? "admin.events.console.orderDetail.ticketRefundRefusedBody"
																: "admin.events.console.orderDetail.ticketRefundUnderWayBody",
														)
													: rd.kind === "failed"
														? this.t(
																rd.stillAdmits
																	? "admin.events.console.orderDetail.ticketRefundFailedStillAdmits"
																	: "admin.events.console.orderDetail.ticketRefundFailedBody",
															)
														: rd.kind === "cancelledNoRefund"
															? this.t(
																	"admin.events.console.orderDetail.ticketCancelledNoRefund",
																)
															: this.t(
																	"admin.events.console.orderDetail.ticketRefundChecking",
																)
									}
								</schmancy-typography>
							</div>
							<schmancy-icon-button
								icon="receipt_long"
								size="sm"
								variant="outlined"
								color="neutral"
								class="col-start-2 justify-self-start flex-none"
								aria-label=${recordLabel}
								${tooltip(recordLabel)}
								@click=${(e: Event) => {
									// This row is, in the refund step, the toggle control for the
									// whole surface — without this the tap would also pick or drop
									// the ticket the operator only meant to look up.
									e.stopPropagation();
									show(
										() => html`
											<div class="w-md max-w-full p-4">
												<order-refunds .orderId=${t.orderId}></order-refunds>
											</div>
										`,
										{ ariaLabel: recordLabel },
									)
										.pipe(takeUntil(this.disconnecting))
										.subscribe();
								}}
							></schmancy-icon-button>
						</div>
					`;
				})}
				${when(
					refundRow !== null
						? refundRow.refusal === null && refundRow.share !== null
						: checkedIn
							? canUndoCheckIn.get()
							: checkable,
					() =>
						when(
							refundRow,
							(r) => html`
								<div class="flex w-full flex-col">
									<schmancy-typography type="title" token="sm" class="tabular-nums">
										${formatCents(r.share ?? 0)}
									</schmancy-typography>
									${when(
										r.faceShare !== null && r.faceShare !== r.share,
										() => html`
											<schmancy-typography
												type="body"
												token="sm"
												class="tabular-nums text-surface-onVariant line-through"
												>${formatCents(r.faceShare ?? 0)}</schmancy-typography
											>
										`,
									)}
								</div>
							`,
							() => html`
								<div class="flex w-full flex-col gap-2">
									${when(
										checkedIn,
										() => html`
											<schmancy-button
												variant="outlined"
												color="neutral"
												size="xs"
												width="full"
												.busy=${this.actions[t.id]?.busy ?? false}
                                                @click=${() => this.undoCheckIn(t, typeName)}
											>
												<schmancy-icon slot="prefix" aria-hidden="true">undo</schmancy-icon>
												${this.t("admin.events.console.orderDetail.undoCheckinLabel")}
											</schmancy-button>
										`,
										() => html`
											<schmancy-button
												variant="outlined"
												color="neutral"
												size="xs"
												width="full"
												.busy=${this.actions[t.id]?.busy ?? false}
                                                @click=${() => this.checkIn(t, typeName)}
											>
												<schmancy-icon slot="prefix" aria-hidden="true">how_to_reg</schmancy-icon>
												${this.t("admin.events.console.orderDetail.checkinLabel")}
											</schmancy-button>
										`,
									)}
                                    <schmancy-typography role="status" type="body" token="sm" class="min-w-0 text-error-default">${this.actions[t.id]?.message ?? ""}</schmancy-typography>
								</div>
							`,
						),
				)}
			</div>
		`;
	}

	/** The invoice opens in place, as an overlay over this sheet, rather than
	 *  navigating away or handing the operator a download they then have to
	 *  find: reading it is a step inside the call they are already on, and the
	 *  order they were looking at is still behind it when they dismiss.
	 *
	 *  It frames the same-origin `blob:` the preview already holds — a
	 *  cross-origin PDF renders nothing in an iframe — and, unlike the
	 *  thumbnail, keeps the viewer's own toolbar, which is where print and
	 *  download live. Until those bytes arrive there is nothing to frame, so
	 *  the browser takes the Storage URL directly and does whatever it does
	 *  with a PDF; that path is the fallback, never the norm. */
	private openInvoice(url: string, previewUrl: string | null): void {
		if (!previewUrl) {
			window.open(url, "_blank", "noopener");
			return;
		}
		show(
			() => html`
				<iframe
					src=${previewUrl}
					title=${this.t("admin.events.console.orderDetail.invoiceHeading")}
					class="h-[80vh] w-[min(92vw,720px)] rounded-2xl border-0"
				></iframe>
			`,
			{ ariaLabel: this.t("admin.events.console.orderDetail.invoiceHeading") },
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	private checkIn(t: TicketRow, typeName: string): void {
		const eventId = consoleEvent.get()?.id;
		if (!eventId || this.actions[t.id]?.busy) return;
		const requestId = crypto.randomUUID();
		this.actions = {
			...this.actions,
			[t.id]: { requestId, busy: true, message: null },
		};
		ticketCheckinRequested$.next({
			requestId,
			eventId,
			ticketId: t.id,
			typeName,
		});
	}

	/** Two beats, never one: the reversal makes an admitted ticket admittable
	 *  again, so the confirm names that consequence rather than the action. */
	private undoCheckIn(t: TicketRow, typeName: string): void {
		const eventId = consoleEvent.get()?.id;
		if (!eventId || this.actions[t.id]?.busy) return;
		const requestId = crypto.randomUUID();
		this.actions = {
			...this.actions,
			[t.id]: { requestId, busy: true, message: null },
		};
		from(
			confirm({
				title: this.t("admin.events.console.orderDetail.undoConfirmTitle"),
				message: this.t("admin.events.console.orderDetail.undoConfirmBody", {
					typeName,
				}),
				confirmText: this.t(
					"admin.events.console.orderDetail.undoCheckinLabel",
				),
				cancelText: this.t("admin.events.console.orderDetail.undoCancel"),
				variant: "danger",
			}),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe((ok) => {
				if (!ok) {
					this.actions = {
						...this.actions,
						[t.id]: { requestId, busy: false, message: null },
					};
					return;
				}
				ticketUndoCheckinRequested$.next({
					requestId,
					eventId,
					ticketId: t.id,
					typeName,
				});
			});
	}

	/** The invoice as the first of the order-wide actions: one tap opens the
	 *  stored PDF in place (`openInvoice`), and its name — the accessible name
	 *  and the tooltip alike — carries what an operator on a call reaches for:
	 *  the § 14 number and the transaction fee the PSP crossing cost (the
	 *  organiser's cost, never a line on the buyer's invoice; on a refunded
	 *  order it says the provider kept it). Where no document exists yet it
	 *  stands disabled and says why, the same refusal vocabulary every action
	 *  here uses. A €0 order carries none: § 14 obliges no invoice and
	 *  `fulfillReceipt` consumes no number for it. */
	private renderInvoiceAction(o: OrderRow) {
		if (!this.showInvoice || orderTotal(o) === 0) return html``;
		const url = invoiceUrl(o);
		const previewUrl = orderInvoicePreviews.value[o.id] ?? null;
		if (url && !previewUrl) {
			orderInvoicePreviewRequested$.next({ orderId: o.id, url });
		}
		const psp =
			o.payment != null && (o.status === "paid" || o.status === "refunded");
		const fee = psp ? Order.feeCents(o) : null;
		const label = [
			url
				? this.t("admin.events.console.orderDetail.invoiceOpen")
				: this.t(
						o.status === "cancelled"
							? "admin.events.console.orderDetail.invoiceNone"
							: "admin.events.console.orderDetail.invoicePending",
					),
			o.invoiceNumber ?? "",
			!psp
				? ""
				: fee === null
					? this.t("admin.events.console.orderDetail.transactionFeePending")
					: this.t(
							o.status === "refunded"
								? "admin.events.console.orderDetail.transactionFeeNotReturned"
								: "admin.events.console.orderDetail.transactionFee",
							{ amount: formatCents(fee) },
						),
		]
			.filter((part) => part !== "")
			.join(" · ");
		return this.action("receipt_long", label, !url, "filled tonal", () => {
			if (url) this.openInvoice(url, previewUrl);
		});
	}

	/** One sheet, two ways out. An address that comes back unchanged is a
	 *  direct send — one tap's worth of risk, so nothing is asked twice. A
	 *  genuinely different one rewrites who this order reaches, which is not
	 *  undoable from the buyer's side, so it passes through a confirm naming
	 *  both halves of the consequence before anything is written. */
	private sendTickets(o: OrderRow): void {
		const eventId = consoleEvent.get()?.id;
		if (!eventId) return;
		show(
			() => html`<order-send-tickets
            .eventId=${eventId}
            .orderId=${o.id}
            .currentEmail=${o.buyerEmail}
            ?canSend=${canViewSaleReports.get()}
            ?canCorrect=${this.showFixEmail && canAmendOrders.get()}
          ></order-send-tickets>`,
			{ ariaLabel: this.t("admin.events.console.orderDetail.sendTicketsAria") },
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	/** Always present once `orderAccessToken` exists — the one honest,
	 *  always-available path to this order's shareable link, native-share
	 *  capable device or not. Disabled, with the honest reason carried in
	 *  its own aria-label/title, when no token was ever minted. */
	private copyOrderLink(o: OrderRow): void {
		if (!o.orderAccessToken || this.copyingLink) return;
		this.copyingLink = true;
		this.actionNotice = "";
		from(
			navigator.clipboard.writeText(
				`${workspaceOrigin.get()}/order#t=${o.orderAccessToken}`,
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe({
				next: () => {
					this.copyingLink = false;
					this.actionNotice = this.t(
						"admin.events.console.orderDetail.toastLinkCopied",
					);
				},
				error: () => {
					this.copyingLink = false;
					this.actionNotice = this.t(
						"admin.events.console.orderDetail.toastLinkCopyFailed",
					);
				},
			});
	}

	/** Native share sheet only — no clipboard fallback, since Copy link is
	 *  that path, explicitly. This button only renders where
	 *  `navigator.share` exists, so a real click here always has a real
	 *  sheet to open. `AbortError` is the user dismissing that sheet, not a
	 *  failure worth a toast for. */
	private shareOrderLink(o: OrderRow): void {
		if (!o.orderAccessToken || this.sharingLink) return;
		this.sharingLink = true;
		const url = `${workspaceOrigin.get()}/order#t=${o.orderAccessToken}`;
		from(navigator.share({ url, text: o.buyerName }))
			.pipe(
				catchError((err) =>
					(err as DOMException)?.name === "AbortError"
						? EMPTY
						: throwError(() => err),
				),
				finalize(() => {
					this.sharingLink = false;
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe({
				error: () =>
					(this.actionNotice = this.t(
						"admin.events.console.orderDetail.toastShareFailed",
					)),
			});
	}

	/** The rasterize target for one ticket's share — the untransformed 384px
	 *  twin `<ticket-card>` mounts under `capture`. It lives in that element's
	 *  own shadow root, so this reaches it through the card rather than
	 *  querying `[data-ticket-canvas]` on this view's own tree. */
	private ticketCanvas(ticketId: string): HTMLElement | null {
		return (
			this.renderRoot
				.querySelector<TicketCard>(
					`[data-ticket-card="${ticketId}"] ticket-card`,
				)
				?.captureNode() ?? null
		);
	}

	/** Kicked off on a ticket card's own share button `pointerdown` — before
	 *  the `click` that actually shares — so the rasterize has a head start
	 *  on the gesture. Mirrors order-access.view.ts's own warmShareCapture. A
	 *  no-op off a non-"valid" ticket or an unmounted card, both of which
	 *  `shareTicket` re-derives on its own if this never ran. */
	private warmShareCapture(ticket: TicketRow): void {
		if (ticket.status !== "valid") return;
		const node = this.ticketCanvas(ticket.id);
		if (!node) return;
		this.warmCapture = {
			ticketId: ticket.id,
			image: captureTicketCardImage(node),
		};
	}

	/** Rasterizes the same mounted card `renderBuyerTickets` renders — what
	 *  the buyer sees is byte-for-byte what gets shared — and hands it to the
	 *  OS share sheet, with a download fallback where file-share is
	 *  unsupported. Mirrors order-access.view.ts's own shareTicket. */
	private async shareTicket(
		ticket: TicketRow,
		eventName: string,
		typeName: string,
	): Promise<void> {
		if (this.sharingTicketId !== null) return;
		this.sharingTicketId = ticket.id;
		try {
			const node = this.ticketCanvas(ticket.id);
			if (!node) throw new Error("share: the ticket card node is not mounted");

			const warm =
				this.warmCapture?.ticketId === ticket.id
					? this.warmCapture.image
					: null;
			this.warmCapture = null;
			const blob = await (warm ?? captureTicketCardImage(node));

			const fileName = ticketShareFileName(eventName, typeName);
			const result = await shareTicketCardImage(
				blob,
				fileName,
				`${typeName} · ${eventName}`,
			);
			if (result === "downloaded")
				this.actionNotice = this.t(
					"admin.events.console.orderDetail.ticketImageDownloadStarted",
				);
		} catch (err) {
			console.error(err);
			this.actionNotice = this.t(
				"storefront.orderAccess.downloadTickets.error",
			);
		} finally {
			this.sharingTicketId = null;
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"order-detail": OrderDetail;
	}
}
